'use client';

/**
 * FormField — label + input + helper + error (theme T5a §10; Complete_app §17.1).
 *
 * Uses `useId` so label + input + helper are ARIA-linked automatically:
 *   - `<label htmlFor>` ↔ `<input id>`
 *   - `aria-describedby` on the input → helper and error message ids
 *   - `aria-invalid` on the input when an error is present
 *
 * Callers render the input via children as a render-prop:
 *
 *     <FormField label="Email" name="email" required error={errors.email} helper="We send the invite here.">
 *       {(props) => <Input type="email" {...props} />}
 *     </FormField>
 *
 * §17.1 indicators: `required` draws an asterisk (and says "required" to a
 * screen reader); `optional` draws a muted "Optional". Use one convention per
 * form — mark whichever is the minority. `name` gives the input a stable id
 * (`field-<name>`) so `ErrorSummary` can link to and focus it. `labelHidden`
 * keeps the label for screen readers only (a compact filter row, a table cell)
 * — never use a placeholder as the only label.
 */
import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useId } from 'react';

import { cn } from './cn';

interface ChildProps {
  id: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}

interface FormFieldProps {
  label: string;
  /** Asterisk next to the label; announced as "required". */
  required?: boolean;
  /** Muted "Optional" next to the label. */
  optional?: boolean;
  /** Form key: fixes the input id to `field-<name>` for ErrorSummary links. */
  name?: string;
  /** Explicit input id (overrides `name`). */
  id?: string;
  /** Error message — presence drives `aria-invalid` + error styling. */
  error?: string | null;
  /** Instructions shown below the field (kept when an error shows). */
  helper?: ReactNode;
  /** Character-count / limit shown on the right of the helper row. */
  counter?: ReactNode;
  /** Visually hide the label (it stays for screen readers). */
  labelHidden?: boolean;
  children: (props: ChildProps) => ReactNode;
  className?: string;
}

/** The id `FormField name="x"` gives its input — what ErrorSummary links to. */
export function fieldDomId(name: string): string {
  return `field-${name.replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

export function FormField({
  label,
  required = false,
  optional = false,
  name,
  id: idProp,
  error,
  helper,
  counter,
  labelHidden = false,
  children,
  className,
}: FormFieldProps) {
  const generated = useId();
  const id = idProp ?? (name ? fieldDomId(name) : generated);
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const describedBy =
    [error ? errorId : null, helper ? helperId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        className={cn(
          'flex items-baseline gap-1 text-sm font-medium text-fg',
          labelHidden && 'sr-only',
        )}
        htmlFor={id}
      >
        <span>{label}</span>
        {required ? (
          <>
            <span aria-hidden className="text-state-danger">
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        ) : null}
        {optional && !required ? (
          <span className="text-xs font-normal text-fg-muted">Optional</span>
        ) : null}
      </label>

      {children({
        id,
        name,
        'aria-describedby': describedBy,
        'aria-invalid': Boolean(error),
        'aria-required': required || undefined,
      })}

      {(helper || error || counter) && (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            {error ? (
              <p className="flex items-start gap-1 text-xs text-state-danger" id={errorId}>
                <AlertCircle aria-hidden className="mt-px h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </p>
            ) : null}
            {helper ? (
              <p className="text-xs text-fg-muted" id={helperId}>
                {helper}
              </p>
            ) : null}
          </div>
          {counter ? (
            <span className="shrink-0 text-xs tabular-nums text-fg-muted">{counter}</span>
          ) : null}
        </div>
      )}
    </div>
  );
}
